'use server';

/**
 * @fileOverview Car service suggestions flow. Allows users to describe a car problem and receive a list of relevant services and workshops.
 *
 * - carServiceSuggestions - A function that takes a description of a car problem and returns a list of suggested services and workshops.
 * - CarServiceSuggestionsInput - The input type for the carServiceSuggestions function.
 * - CarServiceSuggestionsOutput - The return type for the carServiceSuggestions function.
 */

import {ai} from '@/ai/genkit';
import {z} from 'genkit';

const CarServiceSuggestionsInputSchema = z.object({
  problemDescription: z.string().describe('Description of the car problem or maintenance need.'),
});
export type CarServiceSuggestionsInput = z.infer<typeof CarServiceSuggestionsInputSchema>;

const CarServiceSuggestionsOutputSchema = z.object({
  suggestedServices: z.array(
    z.string().describe('A list of suggested services that can address the issue.')
  ),
  suggestedWorkshops: z.array(
    z.string().describe('A list of suggested workshops that can perform the services.')
  ),
});
export type CarServiceSuggestionsOutput = z.infer<typeof CarServiceSuggestionsOutputSchema>;

export async function carServiceSuggestions(input: CarServiceSuggestionsInput): Promise<CarServiceSuggestionsOutput> {
  return carServiceSuggestionsFlow(input);
}

const prompt = ai.definePrompt({
  name: 'carServiceSuggestionsPrompt',
  input: {schema: CarServiceSuggestionsInputSchema},
  output: {schema: CarServiceSuggestionsOutputSchema},
  prompt: `You are a car maintenance expert. Based on the description of the car problem, suggest relevant services and workshops.

Problem Description: {{{problemDescription}}}

Output the suggested services and workshops as arrays of strings.
`,
});

const carServiceSuggestionsFlow = ai.defineFlow(
  {
    name: 'carServiceSuggestionsFlow',
    inputSchema: CarServiceSuggestionsInputSchema,
    outputSchema: CarServiceSuggestionsOutputSchema,
  },
  async input => {
    const {output} = await prompt(input);
    return output!;
  }
);
